/* eslint-disable  unicorn/prefer-dom-node-text-content */
import classNames from 'classnames';
import React, { FC, useCallback, useMemo, useState } from 'react';
import {
  AppConnectionType,
  FinchApps,
  InstantAvailableStatus,
} from '../../../apps/definition';
import { AppState } from '../../../constants';
import { trackEvent } from '../../../extra/sharedMethods';
import { useHasEnterpriseFeatures } from '../../../hooks/subscriptionHooks';
import { UserApp } from '../../../models/User';
import { GOOGLE_APPS } from '../../../redux/selectors';
import { invokeFastApi } from '../../../scripts/apis/fastapi';
import { App } from '../../../scripts/app';
import { AnalyticsEvent } from '../../../scripts/constants/analytics-event';
import { useUserSafe } from '../../../scripts/hooks';
import { useAppName } from '../../../scripts/hooks/apps';
import { calculateIndexingPercentage, logError } from '../../../scripts/utils';
import { LinkFavicon } from '../../controls/LinkFavicon/LinkFavicon';
import { Timestamp } from '../../controls/Timestamp';
import { UserImage } from '../../controls/UserImage/UserImage';
import { UIButton } from '../../controls/ui/UIButton/UIButton';
import { UIIcon } from '../../controls/ui/UIIcon/UIIcon';
import { UITooltip } from '../../controls/ui/UIToolTip/UIToolTip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../shadcn/lib/components/dropdownMenu';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../../shadcn/lib/components/popover';
import { TableCell, TableRow } from '../../shadcn/lib/components/table';
import { useAppInstallController } from '../AppsTable/AppInstallController';
import { isInstantAvailable } from '../AppsTable/AppUtils';
import {
  EnterpriseIcon,
  PaywallModal,
} from '../EnterprisePaywall/EnterprisePaywall';

const notAvailable = <div className="w-4 h-px bg-cloud-20 " />;
const disablingState = (
  <div className="disablingApp">
    <UIIcon name="spinner-animated" />
    <span>Disabling</span>
  </div>
);

const contactAdmin = (
  <UITooltip title="Please contact your admin to reconnect the app.">
    <div className="connectionIssue gap-1">
      <UIIcon name="error" />
      <span>Error</span>
    </div>
  </UITooltip>
);

const PartiallyConnectedContent: FC = () => (
  <div className="connectedApp">
    <UIIcon className="loader" name="spinner-animated" />
    <span>Connecting</span>
  </div>
);

const DisableButton: FC<{ onClick: () => void }> = ({ onClick }) => {
  return (
    <UIButton
      className="connectButton flipActive"
      onClick={onClick}
      size="small"
      type="warning"
    >
      Disconnect
    </UIButton>
  );
};

const ConnectionNotAvailable = ({ tooltipText }: { tooltipText?: string }) => (
  <UIButton
    className="w-[130px]"
    disabled
    disabledReason={tooltipText ?? 'You are unable to add this app.'}
    type="secondary"
  >
    Connect
  </UIButton>
);

const ConnectedContent: FC<{ disableDisconnect?: boolean }> = ({
  disableDisconnect = false,
}) =>
  disableDisconnect ? (
    <UITooltip title="Your admin has connected the app.">
      <div className="connectedApp">
        <UIIcon name="check" />
        <span>Connected</span>
      </div>
    </UITooltip>
  ) : (
    <div className="connectedApp">
      <UIIcon name="check" />
      <span>Connected</span>
    </div>
  );

const IndexingButton: FC<{ connection: UserApp }> = ({ connection }) => {
  const percentage = calculateIndexingPercentage(connection);

  return (
    <div className="indexingApp">
      <UIIcon name="spinner-animated" />
      <span>{percentage === 0 ? 'Syncing' : `syncing (${percentage}%)`}</span>
    </div>
  );
};

const notIndexingState = (
  <div className="connectionIssue">
    <UIIcon name="error" />
    <span>Not indexing</span>
  </div>
);

interface ConnectionsStatusCellProps {
  connections: UserApp[];
}

const ConnectionsStatusCell: FC<ConnectionsStatusCellProps> = ({
  connections,
}) => {
  const hasError = connections.some((connection) => {
    return !(
      connection.statusCode === AppState.Connecting ||
      connection.statusCode === AppState.IndexingComplete
    );
  });

  if (connections.length === 0) {
    return notAvailable;
  }

  return (
    <div className="colConnections flex items-center gap-2">
      <div>
        {connections.length}{' '}
        {connections.length > 1 ? 'connections' : 'connection'}
      </div>
      <svg
        fill="none"
        height="10"
        viewBox="0 0 10 10"
        width="10"
        xmlns="http://www.w3.org/2000/svg"
      >
        <circle
          cx="5"
          cy="5"
          fill={hasError ? '#EA580C' : '#51CF66'}
          r="4.5"
          stroke={hasError ? '#FDBA74' : '#86EFAC'}
        />
      </svg>
    </div>
  );
};

export const OrgAdminButton: FC<{
  app: App;
  connection: UserApp;
  installEventName?: AnalyticsEvent;
}> = ({ app, connection, installEventName }) => {
  const appInstallController = useAppInstallController();

  const { shortname } = app.definition;

  const orgInstallCallback = useCallback(() => {
    const eventBody = {
      app: shortname,
      isOrg: true,
    };

    trackEvent(AnalyticsEvent.AppConnect, eventBody);

    if (installEventName) {
      trackEvent(installEventName, eventBody);
    }

    appInstallController.triggerAppInstallRequest(app, true, connection.id);
  }, [shortname, appInstallController, app, connection.id, installEventName]);

  const orgUninstallCallback = useCallback(() => {
    trackEvent(AnalyticsEvent.AppDisconnect, {
      app: shortname,
      isOrg: true,
    });

    appInstallController.triggerAppUninstallRequest(app, true, connection.id);
  }, [app, appInstallController, connection.id, shortname]);

  const { definition } = app;

  const isInstant = isInstantAvailable(definition, InstantAvailableStatus.Org);

  const isDisabled = useMemo(() => GOOGLE_APPS.has(shortname), [shortname]);

  switch (connection.statusCode) {
    case AppState.PartiallyConnected: {
      return <PartiallyConnectedContent />;
    }

    case AppState.Connecting:
    case AppState.Indexing:
    case AppState.IndexingFailed:
    case AppState.IndexingComplete: {
      if (isInstant || connection.statusCode === AppState.IndexingComplete) {
        // hack to enable frontend to show connected status for instant apps

        if (isDisabled) {
          return <ConnectedContent disableDisconnect />;
        }

        return (
          <div className="flipHover">
            <span className="flipNormal">
              <ConnectedContent />
            </span>
            <DisableButton onClick={orgUninstallCallback} />
          </div>
        );
      }

      return <IndexingButton connection={connection} />;
    }

    case AppState.NotConnected:
    case AppState.ErrorDoNotReconnect:
      return notIndexingState;
    case AppState.ErrorReconnect: {
      return (
        <UIButton
          className="connectButton"
          onClick={orgInstallCallback}
          size="small"
          type="caution"
        >
          <UIIcon name="error" />
          <span>Reconnect</span>
        </UIButton>
      );
    }

    case AppState.Disconnecting: {
      return disablingState;
    }
  }
};

export const PersonalButton: FC<{
  app: App;
  connection: UserApp;
  installEventName?: AnalyticsEvent;
}> = ({ app, connection, installEventName }) => {
  const appInstallController = useAppInstallController();
  const { shortname } = app.definition;

  const installCallback = useCallback(() => {
    const eventBody = {
      app: shortname,
      isOrg: false,
      connection: connection.id,
    };

    trackEvent(AnalyticsEvent.AppConnect, eventBody);

    if (installEventName) {
      trackEvent(installEventName, eventBody);
    }

    appInstallController.triggerAppInstallRequest(app, false, connection.id);
  }, [app, connection.id, shortname, appInstallController, installEventName]);

  const uninstallCallback = useCallback(() => {
    appInstallController.triggerAppUninstallRequest(app, false, connection.id);
    trackEvent(AnalyticsEvent.AppDisconnect, {
      app: shortname,
      connection: connection.id,
      isOrg: false,
    });
  }, [app, appInstallController, connection.id, shortname]);

  const { definition } = app;

  const isInstant = isInstantAvailable(
    definition,
    InstantAvailableStatus.Personal
  );

  switch (connection.statusCode) {
    case AppState.PartiallyConnected: {
      return <PartiallyConnectedContent />;
    }

    case AppState.Connecting:
    case AppState.Indexing:
    case AppState.IndexingFailed:
    case AppState.IndexingComplete: {
      if (isInstant || connection.statusCode === AppState.IndexingComplete) {
        // hack to enable frontend to show connected status for instant apps

        return (
          <div className="flipHover">
            <span className="flipNormal">
              <ConnectedContent />
            </span>
            <DisableButton onClick={uninstallCallback} />
          </div>
        );
      }

      return <IndexingButton connection={connection} />;
    }

    case AppState.NotConnected:
    case AppState.ErrorDoNotReconnect:
      return notIndexingState;
    case AppState.ErrorReconnect: {
      return (
        <UIButton
          className="connectButton"
          onClick={installCallback}
          size="small"
          type="caution"
        >
          <UIIcon name="error" />
          <span className="ml-2">Reconnect</span>
        </UIButton>
      );
    }

    case AppState.Disconnecting: {
      return disablingState;
    }
  }
};

const OrgUserButton: FC<{
  app: App;
  connection: UserApp;
}> = ({ app, connection }) => {
  const { definition } = app;

  const isInstant = isInstantAvailable(definition, InstantAvailableStatus.Org);

  switch (connection.statusCode) {
    case AppState.PartiallyConnected: {
      return <PartiallyConnectedContent />;
    }

    case AppState.Connecting:
    case AppState.Indexing:
    case AppState.IndexingFailed:
    case AppState.IndexingComplete: {
      if (isInstant || connection.statusCode === AppState.IndexingComplete) {
        return <ConnectedContent disableDisconnect />;
      }

      return <IndexingButton connection={connection} />;
    }

    case AppState.NotConnected:
    case AppState.ErrorDoNotReconnect:
    case AppState.ErrorReconnect: {
      return contactAdmin;
    }

    case AppState.Disconnecting: {
      return disablingState;
    }
  }
};

const ConnectButtonV3: FC<{ app: App; connectionsLength?: number }> = ({
  app,
  connectionsLength = 0,
}) => {
  const appInstallController = useAppInstallController();
  const [connectionPopupOpen, setConnectionPopupOpen] = useState(false);
  const [paywallModalOpen, setPaywallModalOpen] = useState(false);
  const { connectionType, shortname } = app.definition;
  const {
    admin,
    orgByOrgId: { usersAppsByOrg },
    usersApps,
  } = useUserSafe();

  const hasEnterpriseFeatures = useHasEnterpriseFeatures();

  const installCallback = useCallback(
    ({ isOrg = false }: { isOrg: boolean }) => {
      appInstallController.triggerAppInstallRequest(app, isOrg);
      trackEvent(AnalyticsEvent.AppConnect, {
        app: shortname,
        isOrg,
      });
    },
    [app, shortname, appInstallController]
  );

  const canConnectOrg = useMemo(() => {
    if (connectionType === AppConnectionType.PersonalOnly) {
      return false;
    }

    if (!admin) {
      return false;
    }

    // Apps that can only have one connection
    if (
      GOOGLE_APPS.has(shortname) &&
      usersAppsByOrg.nodes.some((node) => GOOGLE_APPS.has(node.appName))
    ) {
      return false;
    }

    if (!hasEnterpriseFeatures) {
      return !usersAppsByOrg.nodes.some((node) => node.appName === shortname);
    }

    return true;
  }, [connectionType, admin, shortname, usersAppsByOrg, hasEnterpriseFeatures]);

  const canConnectIndividual = useMemo(() => {
    if (
      connectionType === AppConnectionType.PersonalOnly ||
      connectionType === AppConnectionType.AccessControl
    ) {
      if (!hasEnterpriseFeatures) {
        return !usersApps.nodes.some(
          (node) => node.appName === shortname && node.org === ''
        );
      }

      return true;
    }

    return false;
  }, [connectionType, usersApps, shortname, hasEnterpriseFeatures]);

  const isButtonEnabled = useMemo(
    () =>
      connectionType === AppConnectionType.PersonalOnly ||
      connectionType === AppConnectionType.AccessControl ||
      canConnectOrg,
    [canConnectOrg, connectionType]
  );

  const isPaywalled = useMemo(
    () => !canConnectOrg && !canConnectIndividual && !hasEnterpriseFeatures,
    [canConnectOrg, canConnectIndividual, hasEnterpriseFeatures]
  );

  const orgTooltipText: string = useMemo(() => {
    if (connectionType === AppConnectionType.PersonalOnly) {
      return 'No Org connection allowed for this app';
    }

    return 'Only 1 Org connection allowed for this app';
  }, [connectionType]);

  const handleConnectButtonClick = useCallback(() => {
    if (isPaywalled) {
      setPaywallModalOpen(true);
      return;
    }

    if (admin) {
      setConnectionPopupOpen((prev) => !prev);
    } else {
      installCallback({ isOrg: false });
    }
  }, [admin, installCallback, isPaywalled]);

  // Check if an other finch app is connected if so we don't allow to connect
  if (
    FinchApps.includes(shortname) &&
    usersAppsByOrg.nodes.some(({ appName }) => FinchApps.includes(appName))
  ) {
    return (
      <ConnectionNotAvailable tooltipText="Only one HRIS app is able to be connected at a time. Please disconnect the connected HRIS app in order to connect this app." />
    );
  }

  if (
    shortname === 'notion' &&
    usersApps.nodes.some(({ appName }) => appName === 'notion')
  ) {
    return (
      <ConnectionNotAvailable tooltipText="Only one Notion connection is allowed at a time." />
    );
  }

  if (
    shortname === 'outlook' &&
    usersAppsByOrg.nodes.some((node) => node.appName === 'outlook')
  ) {
    return (
      <ConnectionNotAvailable tooltipText="Only one Outlook connection is allowed at a time." />
    );
  }

  if (
    shortname === 'onedrive' &&
    usersAppsByOrg.nodes.some((node) => node.appName === 'onedrive')
  ) {
    return (
      <ConnectionNotAvailable tooltipText="Only one OneDrive connection is allowed at a time." />
    );
  }

  return (
    <DropdownMenu
      onOpenChange={setConnectionPopupOpen}
      open={connectionPopupOpen}
    >
      <DropdownMenuTrigger asChild>
        <UIButton
          className="connectButtonAppcuesClass w-[130px]"
          disabled={!isButtonEnabled}
          disabledReason={
            isButtonEnabled ? undefined : 'You are unable to add this app.'
          }
          onClick={handleConnectButtonClick}
          type="secondary"
        >
          {isPaywalled && <EnterpriseIcon size={8} />}
          {connectionsLength > 0 ? 'Add' : 'Connect'}
        </UIButton>
      </DropdownMenuTrigger>
      {paywallModalOpen && (
        <PaywallModal
          closeModal={() => {
            setPaywallModalOpen(false);
          }}
          modalClasses="whitespace-normal"
          title="Upgrade and unlock multiple connections per app"
        />
      )}

      <DropdownMenuContent className="font-medium">
        <UITooltip disableHover={canConnectOrg} title={orgTooltipText}>
          <DropdownMenuItem
            className={`${canConnectOrg ? '' : 'opacity-50 focus:bg-white'}`}
            onClick={() => {
              if (!canConnectOrg) return;
              installCallback({ isOrg: true });
            }}
          >
            <div className="flex gap-2 items-center">
              <UIIcon name="company" />
              <span>Org connection</span>
            </div>
          </DropdownMenuItem>
        </UITooltip>
        <UITooltip
          disableHover={canConnectIndividual}
          placement="bottom"
          title="No Individual connections allowed for this app"
        >
          <DropdownMenuItem
            className={`flex gap-2 items-center ${
              canConnectIndividual ? '' : 'opacity-40 focus:bg-white'
            }`}
            onClick={() => {
              if (!canConnectIndividual) return;
              installCallback({ isOrg: false });
            }}
          >
            <UIIcon name="person" />
            <span>Individual connection</span>
          </DropdownMenuItem>
        </UITooltip>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const ConnectionRow: FC<{ app: App; connection: UserApp }> = ({
  app,
  connection,
}) => {
  const user = useUserSafe();
  const { getAppName } = useAppName();

  const [workspaceNameInFocus, setWorkspaceNameInFocus] = useState(false);

  const handleWorkspaceNameKeyPress = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        event.currentTarget.blur();
      }
    },
    []
  );

  const handleWorkspaceNameBlur = useCallback(
    async (event: React.FocusEvent<HTMLDivElement>) => {
      const target = event.target as HTMLDivElement;
      setWorkspaceNameInFocus(false);
      if (target.innerText.trim() === '') {
        // If empty, reset the content to connection.connectionName
        const displayName =
          connection.connectionName || getAppName(connection.id);

        target.innerText = displayName ?? connection.displayName;
      }

      if (
        target.innerText !== connection.connectionName &&
        target.innerText.trim() !== ''
      ) {
        try {
          await invokeFastApi({
            path: `/app/connection_name/${connection.id}`,
            method: 'PATCH',
            body: {
              connection_name: target.innerText,
            },
          });
        } catch (error) {
          logError(error);
        }
      }
    },
    [connection, getAppName]
  );

  const selectAllText = useCallback((element: HTMLDivElement) => {
    const range = document.createRange();
    range.selectNodeContents(element);
    const sel = window.getSelection();
    if (sel) {
      sel.removeAllRanges();
      sel.addRange(range);
    }
  }, []);

  const handleFocus = useCallback(
    (event: React.FocusEvent<HTMLDivElement>) => {
      event.preventDefault();
      setWorkspaceNameInFocus(true);
      selectAllText(event.currentTarget);
    },
    [selectAllText, setWorkspaceNameInFocus]
  );

  return (
    <TableRow className="appsTableRowNew bg-cloud-10" key={connection.id}>
      <TableCell>
        <div className="flex items-center gap-1">
          <div className="flex">
            <UIIcon name={connection.org ? 'company' : 'person'} />
          </div>
          <div
            className="font-medium outline-none cursor-text"
            contentEditable
            onBlur={handleWorkspaceNameBlur}
            onFocus={handleFocus}
            onKeyDown={handleWorkspaceNameKeyPress}
            spellCheck="false"
            suppressContentEditableWarning
          >
            {connection.connectionName || getAppName(connection.id)}
          </div>
          {!workspaceNameInFocus && (
            <ConnectionInfoPopover app={app} connection={connection} />
          )}
        </div>
      </TableCell>
      <TableCell />
      <TableCell />
      <TableCell>
        {connection.org ? (
          user.admin ? (
            <OrgAdminButton app={app} connection={connection} />
          ) : (
            <OrgUserButton app={app} connection={connection} />
          )
        ) : (
          <PersonalButton app={app} connection={connection} />
        )}
      </TableCell>
      <TableCell />
    </TableRow>
  );
};

interface AppsTableRowProps {
  app: App;
}

export const AppsTableRowV3: FC<AppsTableRowProps> = ({ app }) => {
  const {
    definition: {
      shortname,
      displayName: name,
      icon,
      description: personalSearchDescription,
    },
  } = app;

  const user = useUserSafe();

  const [expandRow, setExpandRow] = useState(false);

  const { appPreferences, usersAppsByOrg } = user.orgByOrgId;

  const socialProof = useMemo(() => {
    const socials = appPreferences.recommended_apps_list?.filter(
      (element) => element.name === shortname
    )[0];

    if (
      Array.isArray(socials?.social_info_list) &&
      socials?.social_info_list[0]
    ) {
      const filteredSocials = socials.social_info_list.filter(
        (social, index, self) =>
          index === self.findIndex((t) => t.name === social.name)
      );

      return {
        count: filteredSocials.length,
        social_list_info: filteredSocials,
      };
    }

    return { count: 0, social_list_info: [] };
  }, [appPreferences.recommended_apps_list, shortname]);

  const allConnections = useMemo(() => {
    let connections = usersAppsByOrg.nodes.filter(
      (node) => node.appName === shortname
    );

    connections = [
      ...connections,
      ...user.usersApps.nodes.filter((node) => node.appName === shortname),
    ];

    // unique connections
    connections = connections.filter(
      (value, index, self) => self.findIndex((v) => v.id === value.id) === index
    );

    return connections;
  }, [usersAppsByOrg.nodes, user.usersApps.nodes, shortname]);

  return (
    <>
      <TableRow
        className={classNames('appsTableRowNew', {
          'bg-cloud-10': expandRow && allConnections.length > 0,
          'cursor-pointer': allConnections.length > 0,
        })}
        onClick={() => {
          if (allConnections.length > 0) setExpandRow(!expandRow);
        }}
      >
        <TableCell className="colApp">
          <div className=" flex items-center">
            {icon ?? <UIIcon name={shortname} size={32} type="apps" />}
            <UITooltip
              title={`Search across ${personalSearchDescription as string}`}
            >
              <span>{name}</span>
            </UITooltip>
          </div>
        </TableCell>
        <TableCell>
          <div className="colSocialProof">
            {socialProof.social_list_info.slice(0, 4).map((socialUser) => (
              <div className="userIcons" key={socialUser.email}>
                <UITooltip title={socialUser.email}>
                  <div>
                    <UserImage
                      displayName={socialUser.name}
                      fontSize={12}
                      imageUrl={socialUser.icon}
                      size={24}
                    />
                  </div>
                </UITooltip>
              </div>
            ))}
            {socialProof.count > 4 && (
              <div className="socialProofCount">+{socialProof.count - 4}</div>
            )}
          </div>
          {socialProof.social_list_info.length === 0 && notAvailable}
        </TableCell>
        <TableCell>
          <ConnectionsStatusCell connections={allConnections} />
        </TableCell>
        <TableCell className="colPersonal colButtonContainerNew">
          <ConnectButtonV3
            app={app}
            connectionsLength={allConnections.length}
          />
        </TableCell>
        <TableCell className="content-center min-w-[80px]">
          {allConnections.length > 0 && (
            <UIIcon name={expandRow ? 'arrow-up' : 'arrow-down'} size={24} />
          )}
        </TableCell>
      </TableRow>
      {expandRow &&
        allConnections.map((connection) => (
          <ConnectionRow
            app={app}
            connection={connection}
            key={connection.id}
          />
        ))}
    </>
  );
};

export const ConnectionInfoPopover: FC<{
  app: App;
  connection: UserApp;
}> = ({ app, connection }) => {
  const [popoverOpen, setPopoverOpen] = useState(false);

  return (
    <Popover onOpenChange={setPopoverOpen} open={popoverOpen}>
      <PopoverTrigger asChild>
        <div
          className="flex"
          onMouseEnter={() => {
            setPopoverOpen(true);
          }}
          onMouseLeave={() => {
            setPopoverOpen(false);
          }}
        >
          <UIIcon name="info" />
        </div>
      </PopoverTrigger>
      <PopoverContent className="flex flex-col w-80" side="right">
        <div className="flex justify-between">
          <div className="font-medium">{app.definition.displayName}</div>
          <div>
            {connection.appName === 'website' ? (
              <LinkFavicon
                link={`http://${connection.workspaceName!}`}
                size={20}
              />
            ) : (
              <UIIcon name={connection.appName} type="apps" />
            )}
          </div>
        </div>
        <div className="flex justify-between mt-2 gap-2">
          <div className="text-cloud-40">Connection</div>
          <div className="break-all">
            {connection.org ? 'Org' : 'Individual'}
          </div>
        </div>
        {connection.workspaceName && (
          <div className="flex justify-between mt-2 gap-2">
            <div className="text-cloud-40">Workspace</div>
            <div className="break-all">{connection.workspaceName}</div>
          </div>
        )}
        {connection.email && (
          <div className="flex justify-between mt-2 gap-2">
            <div className="text-cloud-40">Account</div>
            <div>{connection.email}</div>
          </div>
        )}
        {connection.createdAt && (
          <div className="flex justify-between mt-2 gap-2">
            <div className="text-cloud-40">Created</div>
            <Timestamp
              unixTime={Math.floor(
                new Date(connection.createdAt).getTime() / 1000
              )}
            />
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};
